// Путь: D:\QR\QR-WEB\dashboard\src\components\Landing\GiftLanding.jsx
// Название: Gift Landing Page (for Google Ads)
// Версия: 1.0
// Дата: 18.02.2026

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './GiftLanding.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com/api';

const GiftLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  // ============================================
  // Загрузка данных подарка
  // ============================================
  useEffect(() => {
    const fetchGift = async () => {
      try {
        const res = await fetch(`${API_URL}/ad-manager/landing/${slug}`);
        const data = await res.json();
        
        if (data.success && data.data) {
          setGift(data.data);
        } else {
          setError('not_found');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError('network');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchGift();
    }
  }, [slug]);

  // ============================================
  // Скачивание картинки
  // ============================================
  const handleDownload = async () => {
    if (!gift?.imageUrl) return;
    
    setDownloading(true);
    try {
      // Трекинг скачивания
      await fetch(`${API_URL}/ad-manager/landing/${slug}/download`, {
        method: 'POST'
      });

      // Скачивание файла
      const response = await fetch(gift.imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `DARY_Gift_${slug}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      setDownloaded(true);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  // ============================================
  // Состояние загрузки
  // ============================================
  if (loading) {
    return (
      <div className="gift-landing-container">
        <div className="gift-landing-loader">
          <div className="gift-landing-spinner"></div>
          <p>Loading your gift...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // Ошибка — не найден
  // ============================================
  if (error || !gift) {
    return (
      <div className="gift-landing-container">
        <div className="gift-landing-card gift-landing-error">
          <div className="gift-landing-error-icon">😔</div>
          <h2>Gift not found</h2>
          <p>This link may have expired or is invalid.</p>
          <button className="gift-landing-btn-main" onClick={() => navigate('/')}>
            Go to DARY
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // Уже активирован
  // ============================================
  if (gift.isRedeemed) {
    return (
      <div className="gift-landing-container">
        <div className="gift-landing-card">
          <div className="gift-landing-header">
            <span className="gift-landing-logo-icon">🎁</span>
            <h1 className="gift-landing-logo-text">DARY</h1>
          </div>

          <div className="gift-landing-redeemed-badge">
            <span>✅</span> This gift has been claimed!
          </div>

          {gift.imageUrl && (
            <div className="gift-landing-image-wrap">
              <img src={gift.imageUrl} alt="Gift" className="gift-landing-image" />
              <div className="gift-landing-image-overlay">CLAIMED</div>
            </div>
          )}

          <div className="gift-landing-bonus-box">
            <span className="gift-landing-bonus-icon">🎉</span>
            <div>
              <strong>Don't miss out!</strong>
              <p>Install DARY and get a welcome bonus from us!</p>
            </div>
          </div>

          <div className="gift-landing-stores">
            <a 
              href="https://play.google.com/store/apps/details?id=com.dary.app"
              target="_blank" rel="noopener noreferrer"
              className="gift-landing-store-btn"
            >
              <span>▶</span> Google Play
            </a>
            <a 
              href="https://apps.apple.com/app/dary/id0000000000"
              target="_blank" rel="noopener noreferrer"
              className="gift-landing-store-btn gift-landing-store-btn-apple"
            >
              <span>🍎</span> App Store
            </a>
          </div>

          <button className="gift-landing-btn-outline" onClick={() => navigate('/register')}>
            ✨ Create account on web
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // Основной экран — подарок доступен
  // ============================================
  return (
    <div className="gift-landing-container">
      <div className="gift-landing-card">
        {/* Логотип */}
        <div className="gift-landing-header">
          <span className="gift-landing-logo-icon">🎁</span>
          <h1 className="gift-landing-logo-text">DARY</h1>
        </div>

        {/* Бренд */}
        {gift.brandName && (
          <div className="gift-landing-brand">
            {gift.brandLogo && <span className="gift-landing-brand-logo">{gift.brandLogo}</span>}
            <span>Gift from <strong>{gift.brandName}</strong></span>
          </div>
        )}

        {/* Картинка подарка */}
        {gift.imageUrl && (
          <div className="gift-landing-image-wrap">
            <img src={gift.imageUrl} alt="Your gift" className="gift-landing-image" />
          </div>
        )}

        {/* Срочность */}
        <div className="gift-landing-urgency">
          <span className="gift-landing-urgency-icon">⏰</span>
          <span>First one to activate wins the prize!</span>
        </div>

        {/* Шаги */}
        <div className="gift-landing-steps">
          <div className={`gift-landing-step ${downloaded ? 'done' : 'active'}`}>
            <div className="gift-landing-step-num">{downloaded ? '✓' : '1'}</div>
            <div className="gift-landing-step-text">
              <strong>Download the image</strong>
              <small>Save this gift image to your phone</small>
            </div>
          </div>
          <div className={`gift-landing-step ${downloaded ? 'active' : ''}`}>
            <div className="gift-landing-step-num">2</div>
            <div className="gift-landing-step-text">
              <strong>Install DARY app</strong>
              <small>Free on Google Play & App Store</small>
            </div>
          </div>
          <div className="gift-landing-step">
            <div className="gift-landing-step-num">3</div>
            <div className="gift-landing-step-text">
              <strong>Upload & claim your reward!</strong>
              <small>Open app → Upload image → Get bonus</small>
            </div>
          </div>
        </div>

        {/* Кнопка скачивания */}
        <button 
          className="gift-landing-btn-main"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? '⏳ Downloading...' : downloaded ? '✅ Downloaded! Now install the app ↓' : '📥 Download Gift Image'}
        </button>

        {/* Кнопки магазинов */}
        <div className="gift-landing-stores">
          <a 
            href="https://play.google.com/store/apps/details?id=com.dary.app"
            target="_blank" rel="noopener noreferrer"
            className="gift-landing-store-btn"
          >
            <span>▶</span> Google Play
          </a>
          <a 
            href="https://apps.apple.com/app/dary/id0000000000"
            target="_blank" rel="noopener noreferrer"
            className="gift-landing-store-btn gift-landing-store-btn-apple"
          >
            <span>🍎</span> App Store
          </a>
        </div>

        {/* Счётчик */}
        {gift.downloads > 0 && (
          <div className="gift-landing-counter">
            👁 {gift.downloads} {gift.downloads === 1 ? 'person has' : 'people have'} seen this gift
          </div>
        )}

        {/* Уже есть аккаунт */}
        <button className="gift-landing-btn-outline" onClick={() => navigate('/login')}>
          Already have DARY? Log in
        </button>
      </div>
    </div>
  );
};

export default GiftLanding;